import * as React from 'react'
import { Conversion, SizeConversionWithStatus } from 'types/sizeConversion'
import { useTranslation } from 'hooks'
import {
  defaultSizeConversion,
  storeConversionInLocalStorage,
  byPositionThenId
} from 'helpers'
import Toggle from '../atoms/Toggle'
import { StyledDot, ToggleDotContainer } from './SizesGrid.styles'

const SizesGrid: React.FC<SizesGridProps> = ({
  sizeConversions,
  title = null,
  onConversionChange,
  onSizeConversionSelected,
  isStockNotificationSizeOption = false,
  isSizeSetBuilder = false,
  hideAll = false,
  groupType,
  hideConversions = false,
  prefixConversion = false,
  disabledTooltip,
  defaultSizeSystem: initialSelectedSizeSystem
}) => {
  const { locale } = useTranslation()
  const [selectedConversion, setSelectedConversion] =
    React.useState<Conversion>(
      initialSelectedSizeSystem || defaultSizeConversion(locale)
    )

  title = title || `Select ${groupType}`

  const conversions: Conversion[] = ['uk', 'eu', 'us']

  React.useEffect(() => {
    storeConversionInLocalStorage(selectedConversion)
    if (onConversionChange) {
      onConversionChange(selectedConversion)
    }
  }, [selectedConversion, onConversionChange])

  const renderConversionOptions = () => {
    return (
      <Toggle.CollapseContainer hidden={hideAll || hideConversions}>
        {conversions.map((conversion) => (
          <Toggle
            as="span"
            key={conversion}
            onClick={() => {
              setSelectedConversion(conversion)
            }}
            active={conversion === selectedConversion}
            // for tests and styled component 👇
            {...{
              [`data-conversion-${conversion}`]: '',
              'data-active': conversion === selectedConversion ? '' : undefined
            }}
          >
            {conversion}
          </Toggle>
        ))}
      </Toggle.CollapseContainer>
    )
  }

  const matchingConversions = (
    sizeConversion: SizeConversionWithStatus,
    conversion: Conversion
  ) => {
    return sizeConversions.find(
      (sCon) =>
        sCon[conversion] === sizeConversion[conversion] &&
        sCon.id !== sizeConversion.id
    )
  }

  const labelForConversion = (sizeConversion: SizeConversionWithStatus) => {
    let baseLabel: React.ReactNode = sizeConversion[selectedConversion]

    if (prefixConversion) {
      baseLabel = `${selectedConversion.toUpperCase()} ${baseLabel}`
    }

    let fix = ''

    if (matchingConversions(sizeConversion, selectedConversion)) {
      if (!matchingConversions(sizeConversion, 'uk')) {
        fix = `UK ${sizeConversion.uk}`
      } else if (!matchingConversions(sizeConversion, 'eu')) {
        fix = `EU ${sizeConversion.eu}`
      } else if (!matchingConversions(sizeConversion, 'us')) {
        fix = `US ${sizeConversion.us}`
      }
    }

    if (fix) {
      baseLabel = (
        <div>
          {baseLabel}
          <span className="sizes__fix_conversion"> ({fix})</span>
        </div>
      )
    }

    return baseLabel
  }

  const renderSizes = () => {
    return (
      <Toggle.CollapseContainer multiLine hidden={hideAll} data-testid="sizes">
        {[...sizeConversions].sort(byPositionThenId).map((sizeConversion) => (
          <ToggleDotContainer key={sizeConversion.id}>
            <Toggle
              as="label"
              size="large"
              title={sizeConversion.disabled ? disabledTooltip : undefined}
              onClick={() => {
                !isStockNotificationSizeOption &&
                  !isSizeSetBuilder &&
                  onSizeConversionSelected(sizeConversion)
              }}
              active={sizeConversion.active}
              fullWidth
              disabled={sizeConversion.disabled}
              // for tests and styled component 👇
              {...{
                [`data-size-item`]: '',
                ['data-active']: sizeConversion.active ? '' : undefined
              }}
            >
              {labelForConversion(sizeConversion)}
              {isStockNotificationSizeOption && (
                <input
                  type="checkbox"
                  name="stock_notification[size_conversion_id][]"
                  value={sizeConversion.id}
                  onChange={() => onSizeConversionSelected(sizeConversion)}
                  defaultChecked={sizeConversion.active}
                />
              )}
              {isSizeSetBuilder && (
                <input
                  type="checkbox"
                  name="product[size_conversion_ids][]"
                  value={sizeConversion.id}
                  checked={sizeConversion.active}
                  onChange={() => onSizeConversionSelected(sizeConversion)}
                />
              )}
            </Toggle>
            {sizeConversion.premiumShipAvailable && <StyledDot size="s" />}
          </ToggleDotContainer>
        ))}
      </Toggle.CollapseContainer>
    )
  }

  return (
    <>
      <div
        className={`sizes-conversion-wrapper ${
          isSizeSetBuilder ? 'pt-0' : ''
        } ${hideAll ? 'hidden' : ''}`}
      >
        <span
          className={`sizes-conversion-wrapper__title ${
            hideAll ? 'hidden' : ''
          }`}
        >
          {title}:
        </span>
        {renderConversionOptions()}
      </div>
      {renderSizes()}
    </>
  )
}

export default SizesGrid

type SizesGridProps = {
  sizeConversions: SizeConversionWithStatus[]
  title?: string
  onConversionChange?: (conversion: Conversion) => void
  onSizeConversionSelected: (sizeConversion: SizeConversionWithStatus) => void
  isStockNotificationSizeOption?: boolean
  hideAll?: boolean
  groupType?: string
  hideConversions?: boolean
  isSizeSetBuilder?: boolean
  prefixConversion?: boolean
  disabledTooltip?: string
  defaultSizeSystem?: Conversion
}
